import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Link,
  Container,
  Text,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  VStack,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { MdPhone, MdLocationOn, MdAttachMoney } from 'react-icons/md';
import { FaArrowRight } from 'react-icons/fa';
import OptimizedImage from '../common/OptimizedImage';
import { lightGlass } from '../glass';

// Between the nav breakpoint (1400px) and 1600px the menu has to fit in a
// narrowing bar, so type and gaps scale down continuously instead of stepping.
// Each clamp is the line through (1400px -> min) and (1600px -> max), so the
// value shrinks smoothly as the window narrows and pins at both ends.
const NAV_FONT_SIZE = 'clamp(12px, 1.25vw - 5.5px, 14.5px)'; // 12 -> 14.5px
const NAV_GAP = 'clamp(14px, 5vw - 56px, 24px)'; // 14 -> 24px
const NAV_CTA_PX = 'clamp(14px, 3vw - 28px, 20px)'; // 14 -> 20px

const Header = () => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Which drawer group is open, by name — there is more than one now.
  const [expandedGroup, setExpandedGroup] = React.useState(null);
  const [scrolled, setScrolled] = useState(false);

  const isHome = location.pathname === '/';

  // Track scroll so the floating nav gains a solid background once past the hero
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Handle hash scrolling on mount
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const specialties = [
    { name: 'Orthopedics', path: '/specialties/orthopedics' },
    { name: 'Interventional Cardiology', path: '/specialties/cardiovascular' },
    { name: 'Pain Management', path: '/specialties/pain-management' },
    { name: 'Spine', path: '/specialties/spine' },
  ];

  // Grouping the three people pages under one menu keeps the bar at seven
  // items; adding them top-level would crowd it again.
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Patient Portal', path: '/patient-portal' },
    { name: 'About Facility', path: '/about-facility' },
    {
      name: 'Specialties',
      path: '/specialties',
      overviewLabel: 'All Specialties',
      children: specialties,
    },
    {
      name: 'Our Team',
      path: '/about-team',
      overviewLabel: 'Leadership & Staff',
      children: [
        // A section of the team page now, not a page of its own.
        { name: 'Board of Directors', path: '/about-team#board' },
        { name: 'Participating Surgeons', path: '/surgeons' },
      ],
    },
    { name: 'Sponsors', path: '/sponsors' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (item) =>
    location.pathname.startsWith(item.path) ||
    (item.children || []).some((c) => location.pathname === c.path);

  // The nav pill is always a solid white glass pill. Heroes on every page
  // start *below* the header rather than behind it, so a transparent pill
  // leaves white links on the white page background — illegible. `scrolled`
  // only deepens the shadow.
  const solid = true;

  // Router links to the page you're already on are a no-op, so clicking the
  // logo (or the current nav item) appeared to do nothing. Scroll back to the
  // top instead, which is what people expect from a logo click.
  const handleSameRouteClick = (path) => (e) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    onClose();
  };

  const goToPartnership = (e) => {
    e.preventDefault();
    onClose();
    setTimeout(() => {
      if (location.pathname === '/contact') {
        const el = document.getElementById('physician-partnerships');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.href = '/contact#physician-partnerships';
      }
    }, 150);
  };

  return (
    <Box
      // Fixed on every page: sticky would occupy flow space and push each
      // hero down by the tab's height, so the tab would sit above the hero
      // instead of dropping into it.
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      transition="all 0.3s ease"
    >
      {/* One solid white tab hanging from the top edge of the page: flush at
          the top, rounded on the bottom corners only, so it reads as a folder
          tab dropped into the hero. Holds both the contact strip and the nav. */}
      {/* Frosted light glass rather than solid white. Over the home hero
          the photograph shows softly through it; over any other page it
          reads as white. Text stays dark ink either way, which is the reason
          this is light glass and not clear: the note above explains why a
          see-through nav with white links fails on white pages. It firms up
          a little once scrolled, so links stay crisp over busy content. */}
      <Box
        w={{ base: '96vw', nav: '65vw' }}
        mx="auto"
        borderBottomRadius={{ base: '14px', md: '20px' }}
        px={{ base: 2, md: 4 }}
        pt={0}
        pb={{ base: 2, md: 3 }}
        sx={{
          ...lightGlass,
          borderTop: 'none',
          bg: scrolled ? 'rgba(255, 255, 255, 0.86)' : 'rgba(255, 255, 255, 0.74)',
          transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
        }}
      >
        {/* Utility strip — address, phone, and the price-transparency CTA.
            Stays put on scroll: the estimate link is the one thing on the site
            that has to be reachable from anywhere, so the whole tab rides down
            the page rather than collapsing to the nav row. */}
        <Flex
          display={{ base: 'none', md: 'flex' }}
          justify="flex-start"
          align="center"
          gap={6}
          px={6}
          // Trimmed from 3 so the larger estimate button below adds only a
          // few pixels to the tab rather than its full extra height.
          pt={2}
          pb={2}
          // The strip now sits inside the white tab, so it takes dark ink.
          color="ink.700"
        >
          {/* The address drops out on tablet widths so the phone number and
              the larger estimate button keep to one line. It stays in the
              footer and the mobile menu. */}
          <Flex align="center" gap={2} display={{ base: 'none', lg: 'flex' }}>
            <Icon as={MdLocationOn} boxSize={4} color="brand.500" />
            <Text fontSize="13px" fontWeight="500" letterSpacing="0.01em">
              2200 Lucien Way, Suite 100, Maitland, FL 32751
            </Text>
          </Flex>

          <Box w="1px" h="14px" bg="blackAlpha.200" display={{ base: 'none', lg: 'block' }} />

          <Link
            href="tel:+13213213935"
            display="flex"
            alignItems="center"
            gap={2}
            color="brand.600"
            _hover={{ color: 'brand.700', textDecoration: 'none' }}
            transition="color 0.2s"
          >
            <Icon as={MdPhone} boxSize={4} color="brand.500" />
            <Text fontSize="13px" fontWeight="700" letterSpacing="0.01em">
              (321) 321-3935
            </Text>
          </Link>

          {/* Solid amber at the far right of the strip — deliberately the one
              element on the header that is not brand blue, so patients spot it
              without hunting. This is the site's estimate CTA; it replaces the
              button that used to sit in the home hero. */}
          <Link
            as={RouterLink}
            to="/price-transparency"
            onClick={handleSameRouteClick('/price-transparency')}
            ml="auto"
            display="flex"
            alignItems="center"
            // Twice the old size and labeled "Price Transparency" first, at the
            // client's request: upfront pricing is one of the center's clearest
            // advantages, so the button leads with it.
            gap={3}
            pl={2}
            pr={5}
            py={2}
            borderRadius="pill"
            bg="accent.400"
            color="ink.900"
            // Amber stays: it is deliberately the one non-blue element so the
            // estimate is findable. The orange glow shadow is what made it
            // look cheap, so that goes.
            boxShadow="inset 0 1px 0 rgba(255,255,255,0.35)"
            _hover={{
              bg: 'accent.300',
              color: 'ink.900',
              textDecoration: 'none',
              transform: 'translateY(-1px)',
            }}
            transition="all 0.2s"
          >
            <Flex
              w="36px"
              h="36px"
              flexShrink={0}
              borderRadius="full"
              align="center"
              justify="center"
              bg="rgba(255,255,255,0.45)"
            >
              <Icon as={MdAttachMoney} boxSize={5} />
            </Flex>
            <Box lineHeight="1.15" whiteSpace="nowrap">
              <Text fontSize="11px" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase">
                Price Transparency
              </Text>
              <Text fontSize="16px" fontWeight="700" letterSpacing="0.005em">
                Get a Cost Estimate
              </Text>
            </Box>
          </Link>
        </Flex>

        {/* Nav row — transparent; the white comes from the tab wrapper */}
        <Flex
          align="center"
          justify="space-between"
          px={{ base: 3, md: 4 }}
          py={{ base: 2, md: 2 }}
        >
          {/* Logo */}
          <Link
            as={RouterLink}
            to="/"
            onClick={handleSameRouteClick('/')}
            aria-label="Lake Lucien Surgery Center — home"
            display="flex"
            alignItems="center"
            _hover={{ opacity: 0.9 }}
            flexShrink={0}
          >
            <OptimizedImage
              src="/logo-lucien.png"
              alt="Lake Lucien Surgery Center"
              h={{ base: '42px', md: '54px' }}
              objectFit="contain"
              loading="eager"
            />
          </Link>

          {/* Desktop nav */}
          <HStack spacing={NAV_GAP} display={{ base: 'none', nav: 'flex' }} align="center">
            {navItems.map((item) =>
              item.children ? (
                <Menu key={item.name}>
                  <MenuButton
                    as={Box}
                    fontSize={NAV_FONT_SIZE}
                    whiteSpace="nowrap"
                    fontWeight={isGroupActive(item) ? '700' : '600'}
                    color={
                      isGroupActive(item)
                        ? 'brand.600'
                        : solid
                        ? 'ink.800'
                        : 'white'
                    }
                    cursor="pointer"
                    transition="color 0.2s"
                    _hover={{ color: solid ? 'brand.600' : 'white' }}
                  >
                    <Flex align="center" gap={1}>
                      {item.name}
                      <ChevronDownIcon boxSize={4} />
                    </Flex>
                  </MenuButton>
                  <MenuList
                    bg="white"
                    borderRadius="xl"
                    boxShadow="0 20px 50px -20px rgba(15,60,90,0.4)"
                    border="1px solid"
                    borderColor="gray.100"
                    p={2}
                    minW="220px"
                  >
                    <MenuItem
                      as={RouterLink}
                      to={item.path}
                      fontSize="sm"
                      fontWeight="600"
                      color="ink.700"
                      borderRadius="lg"
                      _hover={{ bg: 'brand.50', color: 'brand.700' }}
                      py={3}
                    >
                      {item.overviewLabel}
                    </MenuItem>
                    <Box h="1px" bg="gray.100" my={2} />
                    {item.children.map((specialty) => (
                      <MenuItem
                        key={specialty.name}
                        as={RouterLink}
                        to={specialty.path}
                        fontSize="sm"
                        fontWeight="600"
                        color="ink.800"
                        borderRadius="lg"
                        _hover={{ bg: 'brand.50', color: 'brand.700', pl: 5 }}
                        transition="all 0.2s"
                        py={3}
                      >
                        {specialty.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              ) : (
                <Link
                  key={item.name}
                  as={RouterLink}
                  to={item.path}
                  onClick={handleSameRouteClick(item.path)}
                  fontSize={NAV_FONT_SIZE}
                  whiteSpace="nowrap"
                  fontWeight={isActive(item.path) ? '700' : '600'}
                  color={
                    isActive(item.path)
                      ? 'brand.600'
                      : solid
                      ? 'ink.800'
                      : 'white'
                  }
                  transition="color 0.2s"
                  _hover={{ color: solid ? 'brand.600' : 'white', textDecoration: 'none' }}
                >
                  {item.name}
                </Link>
              )
            )}

            {/* Physician Partnership gradient pill CTA */}
            <Button
              as="a"
              href="/contact#physician-partnerships"
              onClick={goToPartnership}
              size="sm"
              px={NAV_CTA_PX}
              py={3}
              h="auto"
              variant="brandGradient"
              fontSize={NAV_FONT_SIZE}
              fontWeight="600"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Physician Partnership
            </Button>
          </HStack>

          {/* Mobile: quick-call + menu */}
          <Flex display={{ base: 'flex', nav: 'none' }} align="center" gap={1}>
            <IconButton
              as="a"
              href="tel:+13213213935"
              icon={<Icon as={MdPhone} boxSize={5} />}
              variant="ghost"
              aria-label="Call (321) 321-3935"
              color="brand.600"
              _hover={{ bg: 'blackAlpha.100' }}
            />
            <IconButton
              onClick={onOpen}
              icon={<HamburgerIcon boxSize={6} />}
              variant="ghost"
              aria-label="Open menu"
              color="ink.700"
              _hover={{ bg: 'blackAlpha.100' }}
            />
          </Flex>
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay bg="blackAlpha.600" />
        <DrawerContent>
          <DrawerCloseButton size="lg" mt={2} _hover={{ bg: 'gray.100' }} />

          <DrawerBody p={0} mt={16}>
            {/* Mobile Contact Info */}
            <Box bg="brand.50" p={6} mb={4}>
              <VStack align="start" spacing={4}>
                <Box>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={MdLocationOn} boxSize={5} color="brand.500" />
                    <Text fontSize="xs" fontWeight="700" color="ink.600">
                      ADDRESS
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="ink.700" lineHeight="tall">
                    2200 Lucien Way, Suite 100
                    <br />
                    Maitland, FL 32751
                  </Text>
                </Box>
                <Box w="100%">
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={MdPhone} boxSize={5} color="brand.500" />
                    <Text fontSize="xs" fontWeight="700" color="ink.600">
                      PHONE
                    </Text>
                  </Flex>
                  <Link
                    href="tel:+13213213935"
                    fontSize="lg"
                    fontWeight="700"
                    color="brand.600"
                    _hover={{ color: 'brand.500' }}
                  >
                    (321) 321-3935
                  </Link>
                </Box>

                <Button
                  as="a"
                  href="/contact#physician-partnerships"
                  onClick={goToPartnership}
                  w="100%"
                  variant="brandGradient"
                  rightIcon={<Icon as={FaArrowRight} boxSize={4} />}
                >
                  Physician Partnership
                </Button>

                {/* The desktop contact strip is hidden on mobile, so the amber
                    price-transparency link is repeated here rather than buried
                    in the nav list below. */}
                <Button
                  as={RouterLink}
                  to="/price-transparency"
                  onClick={onClose}
                  w="100%"
                  bg="accent.50"
                  color="accent.700"
                  border="1px solid"
                  borderColor="accent.200"
                  fontWeight="700"
                  _hover={{ bg: 'accent.100', color: 'accent.800' }}
                  leftIcon={<Icon as={MdAttachMoney} boxSize={5} />}
                >
                  Price Transparency
                </Button>
              </VStack>
            </Box>

            {/* Mobile Navigation */}
            <VStack spacing={0} align="stretch">
              {navItems.map((item) =>
                item.children ? (
                  <Box key={item.name}>
                    <Box
                      display="block"
                      px={6}
                      py={4}
                      fontSize="sm"
                      fontWeight="700"
                      letterSpacing="wide"
                      textTransform="uppercase"
                      color={isGroupActive(item) ? 'white' : 'ink.700'}
                      bg={isGroupActive(item) ? 'linear-gradient(135deg, #19C2DA, #2196F3)' : 'transparent'}
                      borderLeft="4px solid"
                      borderColor={isGroupActive(item) ? 'brand.400' : 'transparent'}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ bg: isGroupActive(item) ? 'linear-gradient(135deg, #14b7d6, #1e88e5)' : 'gray.50' }}
                      onClick={() =>
                        setExpandedGroup(expandedGroup === item.name ? null : item.name)
                      }
                    >
                      <Flex align="center" justify="space-between">
                        {item.name}
                        <ChevronDownIcon
                          boxSize={5}
                          transform={expandedGroup === item.name ? 'rotate(180deg)' : 'rotate(0deg)'}
                          transition="transform 0.2s"
                        />
                      </Flex>
                    </Box>
                    {expandedGroup === item.name && (
                      <VStack spacing={0} align="stretch" bg="gray.50" pl={4}>
                        <Link
                          as={RouterLink}
                          to={item.path}
                          display="block"
                          px={6}
                          py={3}
                          fontSize="sm"
                          fontWeight="600"
                          color="brand.600"
                          _hover={{ color: 'brand.700', bg: 'white', textDecoration: 'none' }}
                          onClick={onClose}
                        >
                          {item.overviewLabel}
                        </Link>
                        {item.children.map((specialty) => (
                          <Link
                            key={specialty.name}
                            as={RouterLink}
                            to={specialty.path}
                            display="block"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="600"
                            color="ink.800"
                            _hover={{ color: 'brand.600', bg: 'white', textDecoration: 'none' }}
                            onClick={onClose}
                          >
                            {specialty.name}
                          </Link>
                        ))}
                      </VStack>
                    )}
                  </Box>
                ) : (
                  <Link
                    key={item.name}
                    as={RouterLink}
                    to={item.path}
                    px={6}
                    py={4}
                    fontSize="sm"
                    fontWeight="700"
                    letterSpacing="wide"
                    textTransform="uppercase"
                    color={isActive(item.path) ? 'white' : 'ink.700'}
                    bg={isActive(item.path) ? 'linear-gradient(135deg, #19C2DA, #2196F3)' : 'transparent'}
                    borderLeft="4px solid"
                    borderColor={isActive(item.path) ? 'brand.400' : 'transparent'}
                    _hover={{
                      bg: isActive(item.path) ? 'linear-gradient(135deg, #14b7d6, #1e88e5)' : 'gray.50',
                      textDecoration: 'none',
                    }}
                    onClick={handleSameRouteClick(item.path)}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
